import { Button, Drawer, Form, Input, Radio, App } from 'antd'
import { FC, memo, useEffect, useState } from 'react'
import styles from './index.module.scss'
import wallet from '@/services/apis/wallet'
import { useTranslation } from '@/services/i18n'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store/types'
import { resetWalletsQuery } from '@/store/wallet'
import { ReactComponent as TipIcon } from '@/assets/icons/tip.svg'
import { ReactComponent as CloseModalIcon } from '@/assets/icons/closeModal.svg'
import Modal from '@/components/Modal'

interface CreateWalletDrawerProps {
  open: boolean
  onClose: () => void
}

const CreateWalletDrawer: FC<CreateWalletDrawerProps> = ({ open, onClose }) => {
  const { t } = useTranslation()
  const options = [
    {
      label: t('common.asset'),
      value: 1,
    },
    {
      label: 'Web3',
      value: 2,
    },
  ]
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const accountName = Form.useWatch('accountName', form)
  const { message } = App.useApp()
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    if (!open) {
      form.resetFields()
    }
  }, [open])
  const onCreate = async () => {
    if (loading) {
      return
    }
    setLoading(true)
    const res = await wallet.createWallet(form.getFieldsValue(true))
    setLoading(false)
    if (res.success) {
      message.success(t('wallet.createWallet.successTip'))
      onClose()
      dispatch(resetWalletsQuery())
      return
    }
  }
  const onShowTip = () => {
    Modal.info({
      title: t('wallet.createWallet.typeTitle'),
      content: <div className={styles.tipContent}>{t('wallet.createWallet.typeDesc')}</div>,
    })
  }
  const len = accountName?.trim?.()?.length
  return (
    <Drawer
      title={t('wallet.createWallet.title')}
      placement="right"
      open={open}
      onClose={onClose}
      width={560}
      closable={false}
      maskClosable={false}
      extra={
        <div onClick={onClose} className={styles.closeIcon}>
          <CloseModalIcon />
        </div>
      }
      footer={
        <div className={styles.footer}>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="primary" disabled={!len || len > 20} onClick={onCreate} loading={loading}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <Form
        className={styles.form}
        layout="vertical"
        form={form}
        initialValues={{ accountType: 1 }}
      >
        <div>
          <label>
            {t('wallet.createWallet.label')}
            <div className={styles.tipIcon} onClick={onShowTip}>
              <TipIcon />
            </div>
          </label>
          <p>{t('wallet.createWallet.desc')}</p>
          <Form.Item name="accountType" className={styles.accountType}>
            <Radio.Group options={options} />
          </Form.Item>
        </div>
        <Form.Item
          label={t('wallet.createWallet.name')}
          name="accountName"
          rules={[{ max: 20, message: t('wallet.createWallet.maxPlaceholder') ?? '' }]}
        >
          <Input placeholder={t('wallet.createWallet.maxPlaceholder') ?? ''} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

export default memo(CreateWalletDrawer)
